import { HttpResponse } from '@angular/common/http';

/**
 * interfaces
 */
import { IAppResponseModel } from './interfaces';
import { IResponse } from '../../utilities/interfaces';

/**
 * Enhanced HTTP success response with convenient envelope accessors.
 * Extends Angular's `HttpResponse` to provide easier access to the API envelope,
 * mirroring how {@link AppHttpErrorResponse} wraps `HttpErrorResponse`.
 *
 * > **Note:** the API layer must request the full response with
 * > `{ observe: 'response' }` — a plain `HttpClient.get<T>(url)` resolves to the
 * > body only and cannot be wrapped.
 *
 * @template TData - Type of the payload carried by the envelope.
 *
 * @example
 * ```typescript
 * // In the API layer
 * public getUsers(): Observable<AppResponse<IUserInfo[]>> {
 *   return this._http
 *     .get<IResponse<IUserInfo[]>>(UsersConst.LIST_ENDPOINT, { observe: 'response' })
 *     .pipe(map((response) => new AppResponse(response)));
 * }
 *
 * // In the facade layer
 * this._api.getUsers().pipe(
 *   map((response) => response.hasData ? response.data : []),
 * );
 * ```
 */
export class AppResponse<TData> extends HttpResponse<IResponse<TData>>
implements IAppResponseModel<TData> {

    /**
     * Creates an enhanced success response wrapper.
     *
     * @param _response - Original Angular `HttpResponse` carrying the API envelope.
     * @example
     * ```typescript
     * const appResponse = new AppResponse(httpResponse);
     * ```
     */
    constructor(private readonly _response: HttpResponse<IResponse<TData>>) {
        super({
            body:       _response.body,
            headers:    _response.headers,
            status:     _response.status,
            statusText: _response.statusText,
            url:        _response.url === null ? undefined : _response.url
        });
    }

    /**
     * Gets the raw envelope carried in the response body.
     *
     * @returns The API envelope with its data payload and success flag.
     * @example
     * ```typescript
     * const response = new AppResponse(httpResponse);
     * console.log(response.dto.success); // true
     * ```
     */
    public get dto(): IResponse<TData> {
        return this.body as IResponse<TData>;
    }

    /**
     * Gets the payload carried by the envelope.
     *
     * @returns The data payload returned by the API.
     * @example
     * ```typescript
     * const response = new AppResponse<IUserInfo[]>(httpResponse);
     * console.log(response.data.length); // 25
     * ```
     */
    public get data(): TData {
        return this.dto?.data;
    }

    /**
     * Gets the envelope's success flag as returned by the server.
     *
     * @returns `true` when the server reported success, `false` otherwise.
     */
    public get success(): boolean {
        return this.dto?.success === true;
    }

    /**
     * Gets whether the response actually carries usable data.
     * Empty arrays count as "no data" so callers can skip the render path.
     *
     * @returns `false` for a null/undefined payload or an empty array, `true` otherwise.
     * @example
     * ```typescript
     * const response = new AppResponse<IUserInfo[]>(httpResponse);
     * const users = response.hasData ? response.data : [];
     * ```
     */
    public get hasData(): boolean {

        // ES6 Destructuring
        // get data
        const { data } = this;

        // void validation
        if (data == null) return false;

        // treat an empty collection as no data
        if (Array.isArray(data)) return data.length > 0;

        return true;
    }

}
